import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import LatexText from "@/components/LatexText";
import { BookOpenCheck, Trash2, PlusCircle, Upload, FileDown, FileText, CheckCircle2, AlertTriangle } from "lucide-react";
import { DataTable } from "@/components/DataTable";

const QTYPES = [
  { key: "mcq_single", label: "MCQ (single correct)" },
  { key: "mcq_multi", label: "MCQ (multiple correct)" },
  { key: "true_false", label: "True/False" },
  { key: "numerical", label: "Numerical" },
  { key: "integer", label: "Integer" },
  { key: "fill_blank", label: "Fill in the blank" },
  { key: "short", label: "Short answer (subjective)" },
  { key: "long", label: "Long answer (subjective)" },
];

const emptyQ = {
  subject: "", chapter: "", topic: "", difficulty: "medium",
  type: "mcq_single", text: "", options: ["", "", "", ""], correct: [],
  explanation: "", marks: 1, negative_marks: 0, tags: [], attachments: [], bloom: "understand", time_hint_sec: 60,
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function QuestionBankPage() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState(emptyQ);
  const [filters, setFilters] = useState({ subject: "", difficulty: "", qtype: "", q: "" });

  const load = async () => {
    const params = {};
    if (filters.subject) params.subject = filters.subject;
    if (filters.difficulty) params.difficulty = filters.difficulty;
    if (filters.qtype) params.qtype = filters.qtype;
    if (filters.q) params.q = filters.q;
    const { data } = await api.get("/qb/questions", { params });
    setItems(data);
    const { data: s } = await api.get("/qb/stats");
    setStats(s);
  };
  useEffect(()=>{load()},[filters]); // eslint-disable-line

  const save = async (e) => {
    e.preventDefault();
    const clean = { ...form,
      marks: Number(form.marks) || 1,
      negative_marks: Number(form.negative_marks) || 0,
      options: form.type.startsWith("mcq") || form.type === "true_false" ? form.options.filter(x => x) : [],
      correct: Array.isArray(form.correct) ? form.correct : [String(form.correct)],
    };
    try {
      await api.post("/qb/questions", clean);
      setOpen(false); setForm(emptyQ);
      toast.success("Question added");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this question?")) return;
    await api.delete(`/qb/questions/${id}`);
    toast.success("Deleted");
    load();
  };

  const toggleCorrect = (opt) => {
    const has = form.correct.includes(opt);
    setForm({ ...form, correct: has ? form.correct.filter(x => x !== opt) : (form.type === "mcq_multi" ? [...form.correct, opt] : [opt]) });
  };

  const parseCsvPreview = async (file) => {
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) return [];
    // Simple CSV split (won't handle quoted commas perfectly — just for preview)
    const parseRow = (l) => {
      const out = []; let cur = ""; let q = false;
      for (const ch of l) {
        if (ch === '"') { q = !q; continue; }
        if (ch === "," && !q) { out.push(cur); cur = ""; continue; }
        cur += ch;
      }
      out.push(cur);
      return out;
    };
    const header = parseRow(lines[0]).map(h => h.trim());
    return lines.slice(1, 6).map(l => {
      const cells = parseRow(l);
      const row = {};
      header.forEach((h, i) => { row[h] = cells[i] || ""; });
      return row;
    });
  };

  const onPickFile = async (f) => {
    setImportFile(f);
    setImportResult(null);
    if (f) {
      try { setImportPreview(await parseCsvPreview(f)); }
      catch { setImportPreview([]); }
    } else {
      setImportPreview([]);
    }
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${BACKEND}/api/qb/template.csv`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: "include",
      });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "qb_template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download template");
    }
  };

  const runImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post("/qb/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`Imported ${data.imported}, skipped ${data.skipped}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally { setImporting(false); }
  };

  const resetImport = () => {
    setImportFile(null); setImportPreview([]); setImportResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div>
      <PageHeader title="Question Bank" subtitle={stats ? `${stats.total} questions · LaTeX supported` : "Loading…"}
        actions={
          <div className="flex items-center gap-2">
            <Dialog open={importOpen} onOpenChange={(v)=>{ setImportOpen(v); if (!v) resetImport(); }}>
              <DialogTrigger asChild>
                <button className="text-sm px-4 py-2 rounded-full bg-white border border-[#0A66C2] text-[#0A66C2] hover:bg-[#EAF3FB] font-semibold inline-flex items-center gap-2" data-testid="bulk-import-btn">
                  <Upload size={14}/> Bulk import
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Bulk import questions from CSV</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  {!importResult && (
                    <>
                      <div className="p-3 rounded-md bg-[#EAF3FB] border border-[#0A66C2]/20 text-sm">
                        <div className="font-semibold text-[#0A66C2] mb-1 inline-flex items-center gap-2"><FileText size={14}/> CSV format</div>
                        <div className="text-[13px] text-[#000000E6] leading-relaxed">
                          Required columns: <code className="bg-white px-1 rounded">subject</code>, <code className="bg-white px-1 rounded">type</code>, <code className="bg-white px-1 rounded">text</code>. Optional: chapter, topic, difficulty (easy/medium/hard), options, correct, explanation, marks, negative_marks, tags, bloom, time_hint_sec.
                        </div>
                        <div className="text-[12px] text-[#00000099] mt-2">Options &amp; correct are pipe-separated (use <code className="bg-white px-1 rounded">|</code> between values). Valid types: mcq_single, mcq_multi, true_false, numerical, integer, fill_blank, short, long, essay.</div>
                        <button onClick={downloadTemplate} data-testid="download-template-btn" className="mt-2 text-xs text-[#0A66C2] font-semibold inline-flex items-center gap-1 hover:underline">
                          <FileDown size={12}/> Download sample template
                        </button>
                      </div>

                      <div>
                        <label className="kpi-label">CSV file</label>
                        <input
                          type="file"
                          accept=".csv,text/csv"
                          data-testid="import-file-input"
                          ref={fileRef}
                          onChange={(e) => onPickFile(e.target.files?.[0] || null)}
                          className="mt-2 block w-full text-sm text-[#000000E6] file:mr-3 file:px-3 file:py-1.5 file:rounded-full file:border-0 file:bg-[#0A66C2] file:text-white file:font-semibold hover:file:bg-[#004182]"
                        />
                      </div>

                      {importPreview.length > 0 && (
                        <div className="border border-[#E0DFDC] rounded-md p-3" data-testid="import-preview">
                          <div className="text-xs font-semibold text-[#00000099] uppercase tracking-wider mb-2">Preview (first {importPreview.length} rows)</div>
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            {importPreview.map((row, i) => (
                              <div key={i} className="text-xs p-2 bg-[#F4F2EE] rounded" data-testid={`preview-row-${i}`}>
                                <div className="font-semibold text-[#0A66C2]">{row.subject} · {row.type} · {row.difficulty || "medium"}</div>
                                <div className="text-[#000000E6] mt-0.5"><LatexText>{row.text}</LatexText></div>
                                {row.options && <div className="text-[#00000099] mt-0.5">Options: {row.options}</div>}
                                {row.correct && <div className="text-[#057642] mt-0.5">Correct: {row.correct}</div>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {importResult && (
                    <div className="text-center py-4" data-testid="import-result">
                      <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center ${importResult.imported > 0 ? 'bg-[#D1E7DD] text-[#057642]' : 'bg-[#F8D7DA] text-[#CC1016]'}`}>
                        {importResult.imported > 0 ? <CheckCircle2 size={32}/> : <AlertTriangle size={32}/>}
                      </div>
                      <div className="text-lg font-semibold mt-3">{importResult.imported} question(s) imported</div>
                      <div className="text-sm text-[#00000099] mt-1">{importResult.skipped} skipped · from {importResult.filename}</div>
                      {importResult.errors && importResult.errors.length > 0 && (
                        <div className="mt-4 text-left max-h-40 overflow-y-auto bg-[#F8D7DA]/50 border border-[#CC1016]/20 rounded p-3">
                          <div className="text-xs font-semibold text-[#CC1016] mb-1 uppercase tracking-wider">Errors ({importResult.errors.length})</div>
                          {importResult.errors.map((e, i) => (
                            <div key={i} className="text-xs text-[#000000E6]">Row {e.row}: {e.error}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <DialogFooter>
                  {!importResult ? (
                    <button
                      onClick={runImport}
                      disabled={!importFile || importing}
                      data-testid="import-run-btn"
                      className="btn-primary inline-flex items-center gap-2 disabled:opacity-40"
                    >
                      <Upload size={14}/> {importing ? "Importing…" : "Import questions"}
                    </button>
                  ) : (
                    <>
                      <button onClick={resetImport} data-testid="import-another-btn" className="px-4 py-2 rounded-full border border-[#00000033] text-[#000000E6] font-semibold">Import another</button>
                      <button onClick={()=>setImportOpen(false)} className="btn-primary" data-testid="import-close-btn">Close</button>
                    </>
                  )}
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><button className="btn-primary inline-flex items-center gap-2" data-testid="add-question-btn"><PlusCircle size={14}/> New question</button></DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Add question</DialogTitle></DialogHeader>
              <form onSubmit={save} className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <input required placeholder="Subject" data-testid="q-subject-input" className="px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.subject} onChange={e=>setForm({...form, subject:e.target.value})}/>
                  <input placeholder="Chapter" data-testid="q-chapter-input" className="px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.chapter} onChange={e=>setForm({...form, chapter:e.target.value})}/>
                  <input placeholder="Topic" data-testid="q-topic-input" className="px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.topic} onChange={e=>setForm({...form, topic:e.target.value})}/>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <select data-testid="q-type-select" className="px-3 py-2 border border-[#E0DFDC] rounded-md bg-white" value={form.type} onChange={e=>setForm({...form, type:e.target.value, correct:[]})}>
                    {QTYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                  <select data-testid="q-difficulty-select" className="px-3 py-2 border border-[#E0DFDC] rounded-md bg-white" value={form.difficulty} onChange={e=>setForm({...form, difficulty:e.target.value})}>
                    <option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option>
                  </select>
                  <div className="grid grid-cols-2 gap-2">
                    <input type="number" step="0.5" placeholder="Marks" data-testid="q-marks-input" className="px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.marks} onChange={e=>setForm({...form, marks:e.target.value})}/>
                    <input type="number" step="0.25" placeholder="Neg. marks" data-testid="q-neg-input" className="px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.negative_marks} onChange={e=>setForm({...form, negative_marks:e.target.value})}/>
                  </div>
                </div>
                <textarea required placeholder="Question text (LaTeX supported: $\sin x$ or $$E = mc^2$$)" data-testid="q-text-input"
                  className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md min-h-[80px] font-mono text-sm"
                  value={form.text} onChange={e=>setForm({...form, text:e.target.value})}/>
                <div className="p-2 bg-[#F4F2EE] rounded text-sm">
                  <div className="text-[10px] uppercase tracking-wider text-[#00000099] mb-1">Preview</div>
                  <LatexText>{form.text || "…"}</LatexText>
                </div>

                {(form.type.startsWith("mcq") || form.type === "true_false") && (
                  <div className="space-y-2">
                    <div className="text-xs font-semibold text-[#00000099] uppercase tracking-wider">Options {form.type === "mcq_multi" ? "(select all correct)" : "(select the correct one)"}</div>
                    {(form.type === "true_false" ? ["True","False"] : form.options).map((opt, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input type={form.type === "mcq_multi" ? "checkbox" : "radio"} checked={form.correct.includes(opt)} onChange={()=>toggleCorrect(opt)} className="accent-[#0A66C2]"/>
                        {form.type === "true_false" ? (
                          <span className="text-sm">{opt}</span>
                        ) : (
                          <input placeholder={`Option ${String.fromCharCode(65+i)}`} data-testid={`q-opt-${i}-input`}
                            className="flex-1 px-3 py-1.5 border border-[#E0DFDC] rounded-md text-sm font-mono"
                            value={opt} onChange={e=>{
                              const na = [...form.options]; na[i] = e.target.value;
                              // update correct too if changed
                              const oldOpt = form.options[i];
                              const newCorrect = form.correct.map(x => x === oldOpt ? e.target.value : x);
                              setForm({...form, options: na, correct: newCorrect});
                            }}/>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {(form.type === "numerical" || form.type === "integer" || form.type === "fill_blank" || form.type === "short" || form.type === "long") && (
                  <input placeholder="Correct answer" data-testid="q-correct-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.correct[0] || ""} onChange={e=>setForm({...form, correct:[e.target.value]})}/>
                )}

                <textarea placeholder="Explanation (optional)" data-testid="q-explanation-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.explanation} onChange={e=>setForm({...form, explanation:e.target.value})}/>

                <DialogFooter><button type="submit" className="btn-primary" data-testid="q-save-btn">Save question</button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      {/* Stats strip */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="linkedin-card p-3">
            <div className="kpi-label">Total questions</div>
            <div className="text-xl font-bold text-[#0A66C2] mt-1">{stats.total}</div>
          </div>
          <div className="linkedin-card p-3">
            <div className="kpi-label">Subjects</div>
            <div className="text-sm mt-1 flex flex-wrap gap-1">{Object.entries(stats.by_subject).map(([k,v]) => <span key={k} className="text-[11px] px-2 py-0.5 bg-[#EAF3FB] text-[#0A66C2] rounded font-semibold">{k}:{v}</span>)}</div>
          </div>
          <div className="linkedin-card p-3">
            <div className="kpi-label">Difficulty</div>
            <div className="text-sm mt-1 flex flex-wrap gap-1">{Object.entries(stats.by_difficulty).map(([k,v]) => <span key={k} className="text-[11px] px-2 py-0.5 bg-[#F4F2EE] text-[#000000E6] rounded font-semibold">{k}:{v}</span>)}</div>
          </div>
          <div className="linkedin-card p-3">
            <div className="kpi-label">Types</div>
            <div className="text-sm mt-1 flex flex-wrap gap-1">{Object.entries(stats.by_type).map(([k,v]) => <span key={k} className="text-[10px] px-1.5 py-0.5 bg-[#FFF3CD] text-[#915907] rounded font-semibold">{k}:{v}</span>)}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <input placeholder="Search text…" data-testid="qb-search-input" className="px-3 py-2 border border-[#E0DFDC] rounded-md text-sm bg-white" value={filters.q} onChange={e=>setFilters({...filters, q:e.target.value})}/>
        <input placeholder="Subject" data-testid="qb-subject-filter" className="px-3 py-2 border border-[#E0DFDC] rounded-md text-sm bg-white" value={filters.subject} onChange={e=>setFilters({...filters, subject:e.target.value})}/>
        <select data-testid="qb-difficulty-filter" className="px-3 py-2 border border-[#E0DFDC] rounded-md text-sm bg-white" value={filters.difficulty} onChange={e=>setFilters({...filters, difficulty:e.target.value})}>
          <option value="">All difficulties</option><option>easy</option><option>medium</option><option>hard</option>
        </select>
        <select data-testid="qb-type-filter" className="px-3 py-2 border border-[#E0DFDC] rounded-md text-sm bg-white" value={filters.qtype} onChange={e=>setFilters({...filters, qtype:e.target.value})}>
          <option value="">All types</option>
          {QTYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </div>

      <DataTable
        testid="qb-table"
        columns={[
          { key:"text", label:"Question", render: r => <div className="max-w-[500px]"><LatexText>{r.text}</LatexText></div> },
          { key:"subject", label:"Subject" },
          { key:"topic", label:"Topic" },
          { key:"difficulty", label:"Diff", render: r => (
            <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase" style={{background:{easy:"#D1E7DD",medium:"#EAF3FB",hard:"#F8D7DA"}[r.difficulty], color:{easy:"#057642",medium:"#0A66C2",hard:"#CC1016"}[r.difficulty]}}>{r.difficulty}</span>
          )},
          { key:"type", label:"Type" },
          { key:"marks", label:"Marks" },
          { key:"actions", label:"", render: r => (
            <button onClick={()=>del(r.id)} data-testid={`del-q-${r.id}`} className="text-[#CC1016] hover:bg-[#CC1016]/10 p-1 rounded"><Trash2 size={14}/></button>
          )},
        ]}
        rows={items}
      />
    </div>
  );
}
